/* eslint-disable */
import { useEffect, useState } from 'react';
import SelectPicker from 'rsuite/SelectPicker';
import { Copy } from 'lucide-react';
import { BaseModal } from '../BaseModal';
import { notificationServices } from '../../../Services';
import allVendorServices from '../../../Services/Form Services/allvendor.services';

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_ADDR = {
	addressLine1: '',
	addressLine2: '',
	addressCity: '',
	addressState: '',
	addressPinCode: '',
};

const EMPTY_FORM = {
	name: '',
	legalName: '',
	locationName: '',
	gstin: '',
	locationEmail: '',
	billing: { ...EMPTY_ADDR },
	pickup: { ...EMPTY_ADDR },
};

const inputCls = 'w-full rounded-md border border-gray-200 bg-gray-50/60 px-2.5 py-1.5 text-[12px] text-gray-800 placeholder:text-gray-400 transition-all hover:border-amber-300 focus:border-amber-400 focus:bg-white focus:outline-none focus:shadow-[0_0_0_2px_rgba(251,191,36,0.15)]';
const labelCls = 'text-left text-[12px] font-semibold text-gray-700';

const UnregisteredBuyerModal = ({ open, initial, onClose, onSave }) => {
	const [form, setForm] = useState(EMPTY_FORM);
	const [stateList, setStateList] = useState([]);
	// City options per address block, fetched for that block's selected state.
	const [citiesMap, setCitiesMap] = useState({
		billing: [],
		pickup: [],
	});

	useEffect(() => {
		if (!open) return;
		const loc = initial?.locations?.[0];
		setForm({
			name: initial?.name ?? '',
			legalName: initial?.legalName ?? '',
			locationName: loc?.name ?? '',
			gstin: loc?.gstin ?? '',
			locationEmail: loc?.locationEmail ?? '',
			billing: { ...EMPTY_ADDR, ...(loc?.billingAddress ?? {}) },
			pickup: { ...EMPTY_ADDR, ...(loc?.shippingAddress ?? {}) },
		});
		setCitiesMap({ billing: [], pickup: [] });
		// Same source as AddNewVendorModal.
		allVendorServices.fetchStates()
			.then((res) => {
				let list = [];
				if (res && Array.isArray(res)) {
					list = res;
				} else if (res?.statusCode === 200 && res?.data) {
					list = res.data;
				}
				setStateList(list);
				// Edit flow: prefilled states ke liye city options bhi load karo,
				// warna dropdown me saved city dikhti nahi.
				[
					['billing', loc?.billingAddress?.addressState],
					['pickup', loc?.shippingAddress?.addressState],
				].forEach(([which, stateName]) => {
					if (!stateName) return;
					const stateObj = list?.find((st) => st?.name === stateName);
					const stateId = stateObj?.ID ?? stateObj?.id;
					if (!stateId) return;
					allVendorServices.fetchCitiesByState({ stateId })
						.then((cr) => {
							if (cr && Array.isArray(cr)) {
								setCitiesMap((prev) => ({ ...prev, [which]: cr }));
							}
						})
						.catch(() => { });
				});
			})
			.catch(() => { });
	}, [open]);

	const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
	const setAddr = (which, key, value) => setForm((prev) => ({
		...prev,
		[which]: { ...prev[which], [key]: value },
	}));

	const fetchCities = async (stateName, which) => {
		const stateObj = stateList?.find((st) => st?.name === stateName);
		// The states API returns the id as uppercase `ID` (see AddNewVendorModal).
		const stateId = stateObj?.ID ?? stateObj?.id;
		if (!stateId) {
			setCitiesMap((prev) => ({ ...prev, [which]: [] }));
			return;
		}
		try {
			const res = await allVendorServices.fetchCitiesByState({
				stateId,
			});
			if (res && Array.isArray(res)) {
				setCitiesMap((prev) => ({ ...prev, [which]: res }));
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Failed to fetch cities', error);
		}
	};

	const handleStateChange = (which, value) => {
		setForm((prev) => ({
			...prev,
			[which]: { ...prev[which], addressState: value ?? '', addressCity: '' },
		}));
		fetchCities(value, which);
	};

	const copyBillingToPickup = () => {
		setForm((prev) => ({ ...prev, pickup: { ...prev.billing } }));
		setCitiesMap((prev) => ({ ...prev, pickup: prev.billing }));
	};

	const handleConfirm = () => {
		// Sab fields mandatory hain — sirf GSTIN aur Address line 2 optional.
		const requiredFields = [
			[form.name, 'Buyer Name'],
			[form.legalName, 'Legal Name'],
			[form.locationName, 'Location Name'],
			[form.billing.addressLine1, 'Billing Address line 1'],
			[form.billing.addressState, 'Billing State'],
			[form.billing.addressCity, 'Billing City'],
			[form.billing.addressPinCode, 'Billing Pincode'],
			[form.pickup.addressLine1, 'Pickup Address line 1'],
			[form.pickup.addressState, 'Pickup State'],
			[form.pickup.addressCity, 'Pickup City'],
			[form.pickup.addressPinCode, 'Pickup Pincode'],
		];
		const missing = requiredFields.find(([value]) => !String(value ?? '').trim());
		if (missing) {
			notificationServices.generateNotification({
				type: 'error',
				message: `${missing[1]} is mandatory`,
			});
			return;
		}
		if (form.gstin?.trim() && !GST_REGEX.test(form.gstin.trim())) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please enter a valid GST Number',
			});
			return;
		}
		if (form.locationEmail?.trim() && !EMAIL_REGEX.test(form.locationEmail.trim())) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please enter a valid Location Email',
			});
			return;
		}
		onSave({
			name: form.name.trim(),
			legalName: form.legalName.trim(),
			unregistered: true,
			locations: [
				{
					name: form.locationName.trim(),
					gstin: form.gstin?.trim() || null,
					locationEmail: form.locationEmail?.trim() || null,
					billingAddress: { ...form.billing },
					shippingAddress: { ...form.pickup },
				},
			],
		});
	};

	const renderBtns = () => (
		<div className='flex w-full items-center gap-2'>
			<button
				type='button'
				onClick={onClose}
				className='flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-[13px] font-semibold text-gray-700 hover:bg-gray-50'
			>
				Cancel
			</button>
			<button
				type='button'
				onClick={handleConfirm}
				className='flex-1 rounded-md bg-yellow-400 px-4 py-2 text-[13px] font-bold text-black hover:bg-yellow-500'
			>
				Confirm
			</button>
		</div>
	);

	const addrFields = (which) => (
		<div className='flex flex-col gap-2'>
			<input
				className={inputCls}
				placeholder='Address line 1*'
				value={form[which].addressLine1}
				onChange={(e) => setAddr(which, 'addressLine1', e.target.value)}
			/>
			<input
				className={inputCls}
				placeholder='Address line 2 (optional)'
				value={form[which].addressLine2}
				onChange={(e) => setAddr(which, 'addressLine2', e.target.value)}
			/>
			<SelectPicker
				data={(stateList || []).map((val) => ({
					label: val?.name,
					value: val?.name,
				}))}
				value={form[which].addressState || null}
				onChange={(value) => handleStateChange(which, value)}
				placeholder='Select State'
				size='sm'
				placement='topStart'
				style={{ width: '100%' }}
				menuStyle={{ zIndex: 99999 }}
			/>
			<SelectPicker
				data={(citiesMap[which] || []).map((val) => ({
					label: val?.name,
					value: val?.name,
				}))}
				value={form[which].addressCity || null}
				onChange={(value) => setAddr(which, 'addressCity', value ?? '')}
				disabled={!form[which].addressState}
				placeholder='Select City'
				size='sm'
				placement='topStart'
				style={{ width: '100%' }}
				menuStyle={{ zIndex: 99999 }}
			/>
			<input
				className={inputCls}
				placeholder='Pincode*'
				value={form[which].addressPinCode}
				onChange={(e) => setAddr(which, 'addressPinCode', e.target.value)}
			/>
		</div>
	);

	return (
		<BaseModal
			open={open}
			heading='Unregistered Buyer'
			handleClose={onClose}
			renderBtns={renderBtns}
			width='56rem'
			height='38rem'
		>
			<div className='flex flex-col gap-3 p-4 text-left'>
				<p className='m-0 text-[12px] text-gray-500'>
					Details travel with this order only — the buyer is not saved.
				</p>
				<div className='grid grid-cols-2 gap-x-6 gap-y-3'>
					<div className='flex flex-col gap-1'>
						<span className={labelCls}>
							Buyer Name<span className='text-red-600'>*</span>
						</span>
						<input
							className={inputCls}
							placeholder='Enter buyer name'
							value={form.name}
							onChange={(e) => setField('name', e.target.value)}
						/>
					</div>
					<div className='flex flex-col gap-1'>
						<span className={labelCls}>
							Legal Name<span className='text-red-600'>*</span>
						</span>
						<input
							className={inputCls}
							placeholder='Enter legal name'
							value={form.legalName}
							onChange={(e) => setField('legalName', e.target.value)}
						/>
					</div>
					<div className='flex flex-col gap-1'>
						<span className={labelCls}>
							Location Name<span className='text-red-600'>*</span>
						</span>
						<input
							className={inputCls}
							placeholder='e.g. Main Location'
							value={form.locationName}
							onChange={(e) => setField('locationName', e.target.value)}
						/>
					</div>
					<div className='flex flex-col gap-1'>
						<span className={labelCls}>GSTIN (optional)</span>
						<input
							className={inputCls}
							placeholder='Enter GSTIN'
							value={form.gstin}
							onChange={(e) => setField('gstin', e.target.value.toUpperCase())}
						/>
					</div>
					<div className='flex flex-col gap-1'>
						<span className={labelCls}>Location Email (optional)</span>
						<input
							className={inputCls}
							type='email'
							placeholder='Enter location email'
							value={form.locationEmail}
							onChange={(e) => setField('locationEmail', e.target.value)}
						/>
					</div>
				</div>
				<div className='grid grid-cols-2 gap-6'>
					<div className='flex flex-col gap-1.5'>
						<span className={labelCls}>
							Billing Address<span className='text-red-600'>*</span>
						</span>
						{addrFields('billing')}
					</div>
					<div className='flex flex-col gap-1.5'>
						<div className='flex items-center justify-between'>
							<span className={labelCls}>
								Pickup Address<span className='text-red-600'>*</span>
							</span>
							<button
								type='button'
								onClick={copyBillingToPickup}
								title='Copy the billing address here'
								className='flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-gray-600 transition-colors border-yellow-400 bg-yellow-400 '
							>
								<Copy size={12} />
								Same as billing
							</button>
						</div>
						{addrFields('pickup')}
					</div>
				</div>
			</div>
		</BaseModal>
	);
};

export default UnregisteredBuyerModal;
